/**
 * Span detection for {{ … }} template expressions inside a text input.
 *
 * Used by the autocomplete hook: given the full text of the input and the
 * current caret index, decide whether the caret sits inside a {{ … }} pair
 * and, if so, what the substring under the caret is. The autocomplete popup
 * is shown only when the caret is inside such a span.
 *
 * Mirrors the resolver's regex /{{(.+?)}}/ on the backend (non-greedy), so
 * the editor and runtime agree on where one expression ends and the next
 * begins:
 *
 *   "id={{ functions.uuid() }} and {{ trigger.x }}"
 *           ^^^^^^^^^^^^^^^^^^                       ← span A
 *                                  ^^^^^^^^^^^^^^^   ← span B
 *
 * Caret index is the standard JS selectionStart: 0 = before first char.
 * "Inside the span" means the caret is anywhere from just after the opening
 * `{{` up to and including the position just before the closing `}}`.
 */

export interface ExpressionSpan {
  /** Index of the first char AFTER the opening `{{`. */
  start: number
  /** Index of the first char of the closing `}}` (exclusive end). */
  end: number
  /** The substring between the braces, including surrounding whitespace. */
  inner: string
  /** The token immediately under the caret — what we filter suggestions on. */
  token: string
  /** Index of the first char of `token` (so we know what to replace). */
  tokenStart: number
  /** Caret position relative to ``inner`` (i.e. caret - start). */
  caretInInner: number
}

/**
 * Find the {{ … }} span around the caret, if any.
 * Returns ``null`` when the caret is outside every {{ }} pair, when the
 * `{{` is unclosed, or when input is empty.
 */
export const findExpressionSpanAt = (text: string, caret: number): ExpressionSpan | null => {
  if (!text || caret < 0 || caret > text.length) return null

  // Walk forwards through `{{ ... }}` pairs and find the one containing the
  // caret. Brace-depth tracking handles the typing-in-progress case where
  // the author has `{{ outer.{{ partial }} }}` mid-edit: the outer span
  // closes on the OUTER `}}`, not the inner one, so suggestions match the
  // scope the caret is actually in.
  let cursor = 0
  while (cursor < text.length) {
    const openIdx = text.indexOf('{{', cursor)
    if (openIdx === -1) return null
    // Find the matching closer at depth 0.
    let depth = 1
    let scan = openIdx + 2
    let closeIdx = -1
    while (scan < text.length - 1) {
      // eslint-disable-next-line security/detect-object-injection
      if (text[scan] === '{' && text[scan + 1] === '{') {
        depth += 1
        scan += 2
        continue
      }
      // eslint-disable-next-line security/detect-object-injection
      if (text[scan] === '}' && text[scan + 1] === '}') {
        depth -= 1
        if (depth === 0) {
          closeIdx = scan
          break
        }
        scan += 2
        continue
      }
      scan += 1
    }
    if (closeIdx === -1) {
      // Unclosed opener at this depth — treat the caret as being inside
      // this open span only when it sits between `{{` and end-of-string.
      // Useful while the user is actively typing the expression.
      if (caret >= openIdx + 2) {
        return buildSpan(text, openIdx + 2, text.length, caret)
      }
      return null
    }
    // Caret is inside this {{ ... }} when after the opening braces and at
    // or before the start of the closing braces.
    if (caret >= openIdx + 2 && caret <= closeIdx) {
      return buildSpan(text, openIdx + 2, closeIdx, caret)
    }
    cursor = closeIdx + 2
  }
  return null
}

const buildSpan = (text: string, start: number, end: number, caret: number): ExpressionSpan => {
  const inner = text.slice(start, end)
  const caretInInner = caret - start
  // Token under the caret: walk back to the nearest non-identifier char and
  // forward to the next. Includes `.` so `nodes.search-contact.output` is
  // one token. Treats `(` `,` ` ` as separators so inside `upper(trigger.x)`
  // the token is just `trigger.x`. Identifier chars also include `-` since
  // node ids are kebab-case (e.g. `search-contact`).
  const left = caretInInner
  let l = left
  // eslint-disable-next-line security/detect-object-injection
  while (l > 0 && isIdentChar(inner[l - 1])) l--
  let r = left
  // eslint-disable-next-line security/detect-object-injection
  while (r < inner.length && isIdentChar(inner[r])) r++
  return {
    start,
    end,
    // eslint-disable-next-line security/detect-object-injection
    inner,
    token: inner.slice(l, r),
    tokenStart: start + l,
    caretInInner: left,
  }
}

const isIdentChar = (ch: string): boolean => /[A-Za-z0-9_.\-]/.test(ch)
